import { trackEventsViaInternalApi } from '@gonfalon/analytics/trackEventsViaInternalApi';
import type { AnalyticEventData } from '@gonfalon/analytics/types';
import { isTrialAccount } from '@gonfalon/constants/constants';
import { getDogfoodClient } from '@gonfalon/dogfood-client/getDogfoodClient';
import { enableAgentControlFunnelInstrumentation } from '@gonfalon/dogfood-flags/dogfood-flags';

/**
 * Proxies all AgentControl analytics events through the LaunchDarkly internal
 * API (`POST /api/v2/tracking`) so they reach Segment server-side. This avoids
 * ad-blockers that block direct client requests to segment.io / mixpanel.com.
 */
const stringify = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Set) {
    return JSON.stringify([...value]);
  }
  return JSON.stringify(value);
};

/**
 * Properties every AgentControl event carries so funnel analysis can segment by
 * audience and locale without joining other event streams.
 */
const globalProperties = (): AnalyticEventData => ({
  trial_state: isTrialAccount() ? 'trialist' : 'customer',
  ui_locale: typeof navigator === 'undefined' ? undefined : navigator.language,
});

const trackAgentControl = (event: string, attrs: AnalyticEventData = {}, trackInLaunchDarkly = false) => {
  const attrsWithGlobals = enableAgentControlFunnelInstrumentation() ? { ...globalProperties(), ...attrs } : attrs;
  const properties: Record<string, string> = {};
  for (const [key, value] of Object.entries(attrsWithGlobals)) {
    if (value === undefined) {
      continue;
    }
    properties[key] = stringify(value);
  }

  trackEventsViaInternalApi(event, properties).catch(() => {});

  if (trackInLaunchDarkly) {
    getDogfoodClient().track(event);
  }
};

/** Events added by the trial-funnel instrumentation, which only report while the flag is on. */
const trackAgentControlFunnel = (event: string, attrs: AnalyticEventData = {}) => {
  if (!enableAgentControlFunnelInstrumentation()) {
    return;
  }
  trackAgentControl(event, attrs);
};

type AgentControlConfigType = 'agent' | 'completion' | 'judge';

// AI Configs
const trackAIConfigCreateButtonClick = () => trackAgentControl('AgentControl Config Create Button Clicked');
const trackAIConfigSubmitButtonClicked = () => trackAgentControl('AgentControl Config Submit Button Clicked');

const trackAIConfigsLeftNav = () =>
  trackAgentControl('AgentControl Nav Clicked to AgentControl Page', {
    source: 'nav',
    timestamp: Date.now(),
  });
const trackAIMonitoringLeftNav = () => trackAgentControl('AgentControl AI Insights Nav Clicked');
const trackJoinWaitlistButtonClicked = () => trackAgentControl('AgentControl Waitlist Button Clicked');
const trackGiveFeedbackButtonClicked = () => trackAgentControl('AgentControl Give Feedback Clicked');
const trackCreateAIConfigButtonClicked = (location: string) =>
  trackAgentControl('AgentControl Config Create Button Clicked', { location });

const trackEmptyStateCreateAIConfigButtonClicked = () => trackCreateAIConfigButtonClicked('Empty State');
const trackEmptyStateLearnMoreButtonClicked = () =>
  trackAgentControl('AgentControl Config List Empty State Learn More Clicked', { location: 'Empty State' });
const trackEmptyStateSandboxButtonClicked = () =>
  trackAgentControl('AgentControl Config List Empty State Sandbox Clicked', { location: 'Empty State' });

const trackAIConfigCreateCancelButtonClicked = (attrs: { configType?: AgentControlConfigType } = {}) =>
  trackAgentControl('AgentControl Config Create Cancelled', {
    config_type: enableAgentControlFunnelInstrumentation() ? attrs.configType : undefined,
  });

const trackAiConfigCreateStarted = (attrs: { configType: AgentControlConfigType }) =>
  trackAgentControlFunnel('AgentControl Config Create Started', { config_type: attrs.configType });

const trackAiConfigSaveBlocked = (attrs: { guard: 'unsaved_changes_dialog' | 'approval_required' }) =>
  trackAgentControlFunnel('AgentControl Config Save Blocked', { guard: attrs.guard });

const trackSampleConfigModified = (attrs: { action: 'renamed' | 'edited' | 'deleted'; configKey: string }) =>
  trackAgentControlFunnel('AgentControl Sample Config Modified', {
    action: attrs.action,
    config_key: attrs.configKey,
  });

const trackAIConfigCreateEditKeyClicked = () => trackAgentControl('AgentControl Config Create Key Edited');
const trackModelDropdownClickedAndSelected = (selectedOption: {
  id?: string;
  parameters?: {
    maxTokens?: number;
    temperature?: number;
  };
}) => trackAgentControl('AgentControl Config Model Selected', { selectedOption });
const trackPromptTypeDropdownClickedAndSelected = (selectedOption: string) =>
  trackAgentControl('AgentControl Config Prompt Type Selected', { selectedOption });
function trackDiscardChangesButtonClickedOnVariation(isVariationTargeted: boolean) {
  return trackAgentControl('AgentControl Config Discard Changes Clicked', { isVariationTargeted });
}
const trackSaveChangesButtonClickedOnVariation = () => trackAgentControl('AgentControl Config Save Changes Clicked');
const trackRequestApprovalButtonClickedOnVariation = () =>
  trackAgentControl('AgentControl Config Request Approval Clicked');
const trackEditButtonClickedOnVariation = () => trackAgentControl('AgentControl Config Edit Clicked');
const trackPreviewButtonClickedOnPrompt = () => trackAgentControl('AgentControl Config Preview Prompt Clicked');
const trackTemperatureChanged = () => trackAgentControl('AgentControl Config Temperature Changed');
const trackAddAnotherVariationButtonClicked = () => trackAgentControl('AgentControl Config Add Variation Clicked');
const trackDuplicateVariationButtonClicked = () => trackAgentControl('AgentControl Config Duplicate Variation Clicked');
const trackAIConfigNameUpdated = () => trackAgentControl('AgentControl Config Name Updated');
const trackAIConfigDescriptionUpdated = () => trackAgentControl('AgentControl Config Description Updated');
const trackAIConfigTagsUpdated = () => trackAgentControl('AgentControl Config Tags Updated');
const trackAIConfigKeyCopied = (location: string) => trackAgentControl('AgentControl Config Key Copied', { location });
const trackAIConfigDeleteButtonClicked = () => trackAgentControl('AgentControl Config Delete Clicked');
const trackAIConfigCopyLink = () => trackAgentControl('AgentControl Config Link Copied');
const trackAIConfigChangeHistoryViewed = () => trackAgentControl('AgentControl Config Change History Viewed');
const trackAIConfigShowCodeSnippetsClicked = () => trackAgentControl('AgentControl Config Code Snippets Clicked');
const trackAIConfigTargetingTabNavigation = () => trackAgentControl('AgentControl Config Targeting Tab Viewed');
const trackAIConfigMonitoringTabNavigation = () => trackAgentControl('AgentControl Config Monitoring Tab Viewed');
const trackAIConfigPlaygroundsTabNavigation = () => trackAgentControl('AgentControl Config Playground Tab Viewed');
const trackAIConfigVariationsTabNavigation = () => trackAgentControl('AgentControl Config Variations Tab Viewed');
const trackAIConfigRefreshPendingChangesButtonClicked = () =>
  trackAgentControl('AgentControl Config Refresh Pending Changes Clicked');

// AI Monitoring tabs
const trackAIMonitoringTrendsTabNavigation = () => trackAgentControl('AgentControl AI Insights Trends Tab Viewed');

const trackAIConfigVersionNumberClicked = () => trackAgentControl('AgentControl Config Version Number Clicked');
const trackAIConfigsViewHistoryButtonClicked = () => trackAgentControl('AgentControl Config View History Clicked');
const trackAIConfigVersionHistoryDrawerOpened = () =>
  trackAgentControl('AgentControl Config Version History Drawer Opened');
const trackAIVersioningPromoteClicked = () => trackAgentControl('AgentControl Config Version Promoted');

const trackSignupsForAiConfigs = (sdk: string) => {
  trackAgentControl('AgentControl EAP Signup', { sdk });
};

const trackSignupsForAiConfigsWithoutSDK = () => {
  trackAgentControl('AgentControl EAP Signup No SDK');
};

const trackAiConfigMonitoringChartSelected = (chart: string) =>
  trackAgentControl('AgentControl Config Monitoring Chart Selected', { chart });

const trackAiConfigMonitoringTableDataExported = () =>
  trackAgentControl('AgentControl Config Monitoring Table Exported');

const trackAiConfigMonitoringChartDataExported = () =>
  trackAgentControl('AgentControl Config Monitoring Chart Exported');

const trackAiConfigModelRestrictionAdded = () => trackAgentControl('AgentControl Config Model Restriction Added');
const trackAiConfigModelRestrictionRemoved = () => trackAgentControl('AgentControl Config Model Restriction Removed');

const trackAiConfigDisplayPreferencesEvent = (prev: Set<string>, next: 'all' | Set<string | number>) =>
  trackAgentControl('AgentControl Config Display Preferences Changed', { prev, next });

const trackEvaluatorMetricsChartExported = ({ evalKey }: { evalKey: string }) =>
  trackAgentControl('AgentControl Judge Metrics Chart Exported', { evalKey });

const trackEvaluatorMetricsEvalKeyClicked = ({ evalKey }: { evalKey: string }) =>
  trackAgentControl('AgentControl Judge Metrics Eval Key Clicked', { evalKey });

// AI Config variations
const trackAIConfigVariationAddMessage = () => trackAgentControl('AgentControl Variation Message Added');
const trackAIConfigVariationAddReviewerButtonClicked = () => trackAgentControl('AgentControl Variation Reviewer Added');
const trackAIConfigVariationAddVariation = () => trackAgentControl('AgentControl Variation Added');
const trackAIConfigVariationArchiveVariation = () => trackAgentControl('AgentControl Variation Archived');
const trackAIConfigVariationCopyMessageToClipboard = () => trackAgentControl('AgentControl Variation Message Copied');
const trackAIConfigVariationDeleteMessage = () => trackAgentControl('AgentControl Variation Message Deleted');
const trackAIConfigVariationDiscardChanges = () => trackAgentControl('AgentControl Variation Changes Discarded');
const trackAIConfigVariationDuplicateVariation = () => trackAgentControl('AgentControl Variation Duplicated');
const trackAIConfigVariationEditMessageFullScreen = () =>
  trackAgentControl('AgentControl Variation Message Full Screen Edited');
const trackAIConfigVariationSaveSnippetNew = () => trackAgentControl('AgentControl Variation Snippet Saved As New');
const trackAIConfigVariationSaveSnippetExisting = () =>
  trackAgentControl('AgentControl Variation Snippet Saved To Existing');
const trackAIConfigVariationEditKeyButtonClicked = () => trackAgentControl('AgentControl Variation Key Edit Clicked');
const trackAIConfigVariationMessageRoleChanged = () => trackAgentControl('AgentControl Variation Message Role Changed');
const trackAIConfigVariationPublishArchiveToggleChanged = () =>
  trackAgentControl('AgentControl Variation Publish Toggle Changed');
const trackAIConfigVariationRequestApprovalButtonClicked = () =>
  trackAgentControl('AgentControl Variation Approval Requested');
const trackAIConfigVariationRestoreVariation = () => trackAgentControl('AgentControl Variation Restored');
const trackAIConfigVariationReviewAndSaveButtonClicked = () =>
  trackAgentControl('AgentControl Variation Review And Save Clicked');
const trackAIConfigVariationReviewModalCancelled = () =>
  trackAgentControl('AgentControl Variation Review Modal Cancelled');
const trackAIConfigVariationSaveChangesButtonClicked = () => trackAgentControl('AgentControl Variation Saved');
const trackAIConfigVariationVersionNumberClicked = () =>
  trackAgentControl('AgentControl Variation Version Number Clicked');
const trackAIConfigVariationViewHistoryMenu = () => trackAgentControl('AgentControl Variation View History Clicked');

// AI Monitoring Trends
const trackAIMonitoringTrendsDimensionTypeSelected = (dimensionType: string) =>
  trackAgentControl('AgentControl AI Insights Dimension Type Selected', { dimensionType });
const trackAIMonitoringTrendsDimensionOptionsChanged = (count: number) =>
  trackAgentControl('AgentControl AI Insights Dimension Options Changed', { count });
const trackAIMonitoringTrendsMetricSelected = (metrics: string[]) =>
  trackAgentControl('AgentControl AI Insights Metric Selected', { metrics });
const trackAIMonitoringTrendsDatePresetSelected = (preset: string) =>
  trackAgentControl('AgentControl AI Insights Date Preset Selected', { preset });
const trackAIMonitoringTrendsCustomDateRangeSelected = () =>
  trackAgentControl('AgentControl AI Insights Custom Date Range Selected');
const trackAIMonitoringTrendsTableSearched = () => trackAgentControl('AgentControl AI Insights Table Searched');
const trackAIMonitoringTrendsTableSorted = (column: string, direction: string) =>
  trackAgentControl('AgentControl AI Insights Table Sorted', { column, direction });
const trackAIMonitoringTrendsTableRowExpanded = () => trackAgentControl('AgentControl AI Insights Table Row Expanded');
const trackAIMonitoringTrendsConfigLinkClicked = () =>
  trackAgentControl('AgentControl AI Insights Config Link Clicked');
const trackAIMonitoringTrendsAgentGraphLinkClicked = () =>
  trackAgentControl('AgentControl AI Insights Agent Graph Link Clicked');
const trackAIMonitoringTrendsChartZoomed = () => trackAgentControl('AgentControl AI Insights Chart Zoomed');

// AI Config sidebar (judges)
const trackAIConfigEventKeyEditButtonClicked = () => trackAgentControl('AgentControl Judge Event Key Edit Clicked');
const trackAIConfigEventKeyUpdated = () => trackAgentControl('AgentControl Judge Event Key Updated');
const trackAIConfigEventDirectionEditButtonClicked = () =>
  trackAgentControl('AgentControl Judge Event Direction Edit Clicked');
const trackAIConfigEventDirectionUpdated = () => trackAgentControl('AgentControl Judge Event Direction Updated');

const trackAiOnboardingLoad = () =>
  trackAgentControl('AgentControl Welcome Onboarding Page Loaded', { timestamp: Date.now() });

const trackAiConfigsSignupCompleted = () =>
  trackAgentControl('AgentControl Signup Completed', { timestamp: Date.now() });

const trackAiOnboardingChoiceMade = (choice: 'sdk-setup' | 'playground' | 'create-config') => {
  const eventNames: Record<typeof choice, string> = {
    'sdk-setup': 'AgentControl Onboarding Setup SDK Clicked',
    playground: 'AgentControl Onboarding Playground Clicked',
    'create-config': 'AgentControl Onboarding Create Config Clicked',
  };
  trackAgentControl(eventNames[choice], { timestamp: Date.now() });
};
const trackAiSdkKeyCopied = () => trackAgentControl('AgentControl Config SDK Key Copied');

const trackAiConfigsSampleAppDownloaded = (language: string, platform: string) =>
  trackAgentControl('AgentControl Sample App Downloaded', { language, platform, timestamp: Date.now() });

const trackAiConfigsSdkModelProviderChosen = (provider: string) =>
  trackAgentControl('AgentControl SDK Model Provider Chosen', { provider });

const trackAiConfigsSdkFrameworkChosen = (framework: string) =>
  trackAgentControl('AgentControl SDK Framework Chosen', { framework });

const trackAiConfigsSdkAgenticQuickstartTabViewed = () =>
  trackAgentControl('AgentControl SDK Agentic Quickstart Tab Viewed');

const trackAiConfigsSdkManualSetupTabViewed = () => trackAgentControl('AgentControl SDK Manual Setup Tab Viewed');

const trackAiConfigsSdkSampleAppTabViewed = () => trackAgentControl('AgentControl SDK Sample App Tab Viewed');

const trackAiConfigsSdkAgentInstructionsCopied = () => trackAgentControl('AgentControl SDK Agent Instructions Copied');

const trackAiConfigsSetupSdkViewed = (attrs: { entrySource: 'welcome_flow' | 'nav' | 'deep_link' }) =>
  trackAgentControlFunnel('AgentControl Setup SDK Viewed', { entry_source: attrs.entrySource });

const trackAiConfigsSetupDeferred = () => trackAgentControlFunnel('AgentControl Setup Deferred');

const trackAiConfigsSetupContinueClicked = () => trackAgentControlFunnel('AgentControl Setup Continue Clicked');

const trackAiConfigsSdkSetupTabSelected = (attrs: { tab: 'agent' | 'manual' | 'sample' }) =>
  trackAgentControlFunnel('AgentControl SDK Setup Tab Selected', { tab: attrs.tab });

const trackAiConfigsInstallPromptCopied = (attrs: {
  surface: 'setup_sdk' | 'flag_drawer';
  snippet: 'install' | 'initialize' | 'environment';
}) => trackAgentControlFunnel('AgentControl Install Prompt Copied', { surface: attrs.surface, snippet: attrs.snippet });

const trackAiSdkSetupCompleted = (language: string, provider: string) =>
  trackAgentControl('AgentControl SDK Setup Completed', { language, provider });

const trackAiPlaygroundToSdkConversion = () => trackAgentControl('AgentControl Playground To SDK Conversion');
// Fired when someone who owns a Try It Live sample config enters the SDK setup flow ("Connect your
// app") — the post-demo conversion that Try It Live is meant to drive. Mirrors the Playground event.
const trackTryItLiveToSdkConversion = () => trackAgentControl('AgentControl Try It Live To SDK Conversion');

const trackAgentControlBannerClicked = (attrs: { location: string; referrer: string; projectId: string }) =>
  trackAgentControl('AgentControl Banner Clicked to AgentControl Page', {
    source: 'banner',
    location: attrs.location,
    referrer: attrs.referrer,
    project_id: attrs.projectId,
    timestamp: Date.now(),
  });

const trackAgentControlOptInEnabled = (attrs: { projectId: string; location: string; referrer: string }) =>
  trackAgentControl('AgentControl Opt-in Enabled', {
    project_id: attrs.projectId,
    location: attrs.location,
    referrer: attrs.referrer,
    timestamp: Date.now(),
  });

const trackAgentControlConfigEvaluated = (attrs: {
  eventType: string;
  language: string;
  platform: string;
  sdkVersion: string;
  projectId: string;
  configId: string;
}) =>
  trackAgentControl('AgentControl Config Evaluated', {
    event_type: attrs.eventType,
    language: attrs.language,
    platform: attrs.platform,
    sdk_version: attrs.sdkVersion,
    project_id: attrs.projectId,
    config_id: attrs.configId,
    timestamp: Date.now(),
  });

const trackAgentControlRunReceived = (attrs: {
  eventType: string;
  language: string;
  platform: string;
  sdkVersion: string;
  projectId: string;
  configId: string;
  runId: string;
}) =>
  trackAgentControl('AgentControl Run Received', {
    event_type: attrs.eventType,
    language: attrs.language,
    platform: attrs.platform,
    sdk_version: attrs.sdkVersion,
    project_id: attrs.projectId,
    config_id: attrs.configId,
    run_id: attrs.runId,
    timestamp: Date.now(),
  });

const trackAiConfigsPlaygroundApiKeyModalShown = (evaluationId: string) =>
  trackAgentControl('AgentControl Playground API Key Modal Shown', { evaluation_id: evaluationId });

const trackAiConfigsPlaygroundProviderClicked = (attrs: { evaluationId: string; provider: string }) =>
  trackAgentControl('AgentControl Playground Provider Clicked', {
    evaluation_id: attrs.evaluationId,
    provider: attrs.provider,
  });

const trackAiConfigsPlaygroundProviderApiKeyEntered = (attrs: { evaluationId: string; provider: string }) =>
  trackAgentControl('AgentControl Playground Provider API Key Entered', {
    evaluation_id: attrs.evaluationId,
    provider: attrs.provider,
  });

const trackAiConfigsPlaygroundProviderSettingsSaved = (attrs: { evaluationId: string; provider: string }) =>
  trackAgentControl('AgentControl Playground Provider Settings Saved', {
    evaluation_id: attrs.evaluationId,
    provider: attrs.provider,
  });

const trackAiConfigsPlaygroundModelPicked = (attrs: { evaluationId: string; model: string }) =>
  trackAgentControl('AgentControl Playground Model Picked', {
    evaluation_id: attrs.evaluationId,
    model: attrs.model,
  });

const trackAiConfigsPlaygroundPromptEdited = (attrs: { evaluationId: string; before: string; after: string }) =>
  trackAgentControl('AgentControl Playground Prompt Edited', {
    evaluation_id: attrs.evaluationId,
    before: attrs.before,
    after: attrs.after,
  });

const trackAiConfigsPlaygroundRun = (attrs: {
  evaluationId: string;
  result: string;
  experience?: string;
  /** Whether starting the run succeeded; terminal run results are reported by run completed. */
  outcome?: 'succeeded' | 'failed';
  retry?: boolean;
}) => {
  const funnelEnabled = enableAgentControlFunnelInstrumentation();
  // Failed run starts are new event volume for an existing metric, so they only report while the flag is on.
  if (attrs.outcome === 'failed' && !funnelEnabled) {
    return;
  }
  trackAgentControl('AgentControl Playground Run', {
    evaluation_id: attrs.evaluationId,
    result: attrs.result,
    // The Playground provider experience (byok | trial | none) so we can count
    // how many runs are started under the LaunchDarkly-provided trial key.
    experience: attrs.experience,
    outcome: funnelEnabled ? attrs.outcome : undefined,
    retry: funnelEnabled ? attrs.retry : undefined,
  });
};

const trackAiConfigsPlaygroundFirstRowRendered = (attrs: { playgroundId: string }) =>
  trackAgentControl('AgentControl Playground first row rendered', {
    playground_id: attrs.playgroundId,
  });

const trackAiConfigsPlaygroundRunCompleted = (attrs: {
  playgroundId: string;
  compareColumnCount: number;
  experience?: string;
  totalTokens?: number;
}) =>
  trackAgentControl('AgentControl Playground run completed', {
    playground_id: attrs.playgroundId,
    compare_column_count: attrs.compareColumnCount,
    // The Playground provider experience (byok | trial | none) so we can count
    // how many runs finish under the LaunchDarkly-provided trial key.
    experience: attrs.experience,
    // Total tokens (generation + evaluation) consumed across the run's compare
    // columns, summed from each run's summary. Undefined when no summary was
    // available to read. For trial runs this is the spend against our daily cap.
    total_tokens: attrs.totalTokens,
  });

const trackPlaygroundsListRowOpened = (attrs: { fromTab: 'active' | 'archived'; isArchivedRow: boolean }) =>
  trackAgentControl('AgentControl Playground Row Opened', attrs);

const trackPlaygroundsListArchiveToggled = (attrs: { action: 'archive' | 'restore' }) =>
  trackAgentControl('AgentControl Playground Archive Toggled', attrs);

const trackNewPlaygroundClicked = (attrs: { source: string }) =>
  trackAgentControl('AgentControl Playground New Clicked', attrs);

const trackPlaygroundRunClicked = (attrs: { compareColumnCount: number; label: 'Run' | 'Run all' }) =>
  trackAgentControl('AgentControl Playground Run Clicked', attrs);

const trackPlaygroundDatasetQuickRunSelected = () =>
  trackAgentControl('AgentControl Playground Dataset Quick Run Selected', {});

const trackPlaygroundRunSingleColumnSelected = (attrs: { columnIndex: number }) =>
  trackAgentControl('AgentControl Playground Single Column Run Selected', attrs);

const trackPlaygroundRunStopped = (attrs: { compareColumnCount: number }) =>
  trackAgentControl('AgentControl Playground Run Stopped', attrs);

const trackPlaygroundCompareAdded = (attrs: { nextCompareColumnCount: number }) =>
  trackAgentControl('AgentControl Playground Compare Added', attrs);

const trackPlaygroundRestoredFromBanner = () => trackAgentControl('AgentControl Playground Restored From Banner');

const trackPlaygroundDatasetSelected = (attrs: { hadDatasetBefore: boolean }) =>
  trackAgentControl('AgentControl Playground Dataset Selected', attrs);

const trackPlaygroundSaveAsNewAiConfigMenuClicked = (attrs: { playgroundColumnIndex?: number } = {}) =>
  trackAgentControl('AgentControl Playground Save As New Config Clicked', attrs);

const trackPlaygroundSaveAsCreateVariationClicked = () =>
  trackAgentControl('AgentControl Playground Save As Create Variation Clicked', {});

const trackPlaygroundSaveAsCreateConfigClicked = () =>
  trackAgentControl('AgentControl Playground Save As Create Config Clicked', {});

const trackPlaygroundSaveAsViewConfigClicked = (attrs: { origin: 'save_as_new_config' | 'save_as_variation' }) =>
  trackAgentControl('AgentControl Playground Save As View Config Clicked', attrs);

const trackPlaygroundOfflineRunTriggered = (attrs: { datasetId: string; compareColumnCount: number }) =>
  trackAgentControl('AgentControl Playground Offline Run Triggered', attrs);

const trackPlaygroundLoadConfigClicked = () => trackAgentControl('AgentControl Playground Load Config Clicked');
const trackPlaygroundConfigLoaded = () => trackAgentControl('AgentControl Playground Config Loaded');

const trackConfigOpenInPlaygroundClicked = () => trackAgentControl('AgentControl Config Open In Playground Clicked');
const trackConfigTryInPlaygroundClicked = (attrs: { provider: string }) =>
  trackAgentControl('AgentControl Config Try In Playground Clicked', attrs);
const trackConfigPlaygroundOpened = (attrs: { playgroundName: string; type: 'existing' | 'new' }) =>
  trackAgentControl('AgentControl Config Playground Opened', attrs);

const trackAiConfigsPlaygroundPageViewed = () => trackAgentControl('AgentControl Playground List Page Viewed');
const trackAiConfigsPlaygroundDetailPageViewed = (attrs: { playgroundId: string }) =>
  trackAgentControl('AgentControl Playground Detail Page Viewed', { playground_id: attrs.playgroundId });

// Playground onboarding guide
const trackPlaygroundOnboardingWelcomeModalViewed = () =>
  trackAgentControl('AgentControl Playground Onboarding Welcome Modal Viewed');
const trackPlaygroundOnboardingWelcomeSlideViewed = (attrs: { slideIndex: number; slideName: string }) =>
  trackAgentControl('AgentControl Playground Onboarding Welcome Slide Viewed', {
    slide_index: attrs.slideIndex,
    slide_name: attrs.slideName,
  });
const trackPlaygroundOnboardingGetStartedClicked = () =>
  trackAgentControl('AgentControl Playground Onboarding Get Started Clicked');
const trackPlaygroundOnboardingWelcomeModalDismissed = () =>
  trackAgentControl('AgentControl Playground Onboarding Welcome Modal Dismissed');
const trackPlaygroundOnboardingStepViewed = (attrs: { stepIndex: number; stepName: string }) =>
  trackAgentControl('AgentControl Playground Onboarding Step Viewed', {
    step_index: attrs.stepIndex,
    step_name: attrs.stepName,
  });
const trackPlaygroundOnboardingStepNavigation = (attrs: {
  direction: 'next' | 'back';
  fromStep: number;
  toStep: number;
}) =>
  trackAgentControl('AgentControl Playground Onboarding Step Navigation', {
    direction: attrs.direction,
    from_step: attrs.fromStep,
    to_step: attrs.toStep,
  });
const trackPlaygroundOnboardingCompleted = () => trackAgentControl('AgentControl Playground Onboarding Completed');
const trackPlaygroundOnboardingConfigSaved = (attrs: { configKey: string }) =>
  trackAgentControl('AgentControl Playground Onboarding Config Saved', { config_key: attrs.configKey });
const trackPlaygroundOnboardingConnectAppClicked = (attrs: { configKey: string }) =>
  trackAgentControl('AgentControl Playground Onboarding Connect App Clicked', { config_key: attrs.configKey });

// Try It Live onboarding guide
const trackTryItLiveOnboardingWelcomeModalViewed = () =>
  trackAgentControl('AgentControl Try It Live Onboarding Welcome Modal Viewed', {}, true);
const trackTryItLiveOnboardingWelcomeSlideViewed = (attrs: { slideIndex: number; slideName: string }) =>
  trackAgentControl('AgentControl Try It Live Onboarding Welcome Slide Viewed', {
    slide_index: attrs.slideIndex,
    slide_name: attrs.slideName,
  });
const trackTryItLiveOnboardingGetStartedClicked = () =>
  trackAgentControl('AgentControl Try It Live Onboarding Get Started Clicked', {}, true);
const trackTryItLiveOnboardingWelcomeModalDismissed = () =>
  trackAgentControl('AgentControl Try It Live Onboarding Welcome Modal Dismissed', {}, true);
const trackTryItLiveOnboardingStepViewed = (attrs: { stepIndex: number; stepName: string }) =>
  trackAgentControl('AgentControl Try It Live Onboarding Step Viewed', {
    step_index: attrs.stepIndex,
    step_name: attrs.stepName,
  });
const trackTryItLiveOnboardingStepNavigation = (attrs: {
  direction: 'next' | 'back';
  fromStep: number;
  toStep: number;
}) =>
  trackAgentControl('AgentControl Try It Live Onboarding Step Navigation', {
    direction: attrs.direction,
    from_step: attrs.fromStep,
    to_step: attrs.toStep,
  });
const trackTryItLiveOnboardingCompleted = () =>
  trackAgentControl('AgentControl Try It Live Onboarding Completed', {}, true);

// Try It Live — sample config entry points. The `signup` source has no CTA to click (the
// post-signup redirect creates the config on the user's behalf), so it can only report a creation.
type SampleConfigCtaSource = 'empty-state' | 'nth-user-callout';
type SampleConfigSource = SampleConfigCtaSource | 'signup';
const trackSampleConfigCtaClicked = (attrs: { source: SampleConfigCtaSource }) =>
  trackAgentControl('AgentControl Sample Config CTA Clicked', { source: attrs.source });
const trackSampleConfigCreated = (attrs: {
  source: SampleConfigSource;
  outcome: 'created' | 'already-existed' | 'error';
  configKey?: string;
}) =>
  trackAgentControl('AgentControl Sample Config Created', {
    source: attrs.source,
    outcome: attrs.outcome,
    config_key: attrs.configKey,
  });
const trackNthUserSampleCalloutViewed = () => trackAgentControl('AgentControl Nth User Sample Callout Viewed');
const trackNthUserSampleCalloutDismissed = () => trackAgentControl('AgentControl Nth User Sample Callout Dismissed');

// Try It Live — drawer & chat
const trackTryItLiveDrawerOpened = (attrs: {
  configKey: string;
  duringTour: boolean;
  /** `seeded`: the drawer auto-sends the flag's first user turn on open; `empty`: it opens with a blank chat. */
  seedState: 'seeded' | 'empty';
  panelCount: number;
}) =>
  trackAgentControl('AgentControl Try It Live Drawer Opened', {
    config_key: attrs.configKey,
    during_tour: attrs.duringTour,
    seed_state: attrs.seedState,
    panel_count: attrs.panelCount,
  });
const trackTryItLiveMessageSent = (attrs: {
  turnIndex: number;
  usedPrefill: boolean;
  panelCount: number;
  duringTour: boolean;
}) =>
  trackAgentControl('AgentControl Try It Live Message Sent', {
    turn_index: attrs.turnIndex,
    used_prefill: attrs.usedPrefill,
    panel_count: attrs.panelCount,
    during_tour: attrs.duringTour,
  });
const trackTryItLiveResponseReceived = (attrs: { variationKey: string; turnIndex: number; latencyMs: number }) =>
  trackAgentControl('AgentControl Try It Live Response Received', {
    variation_key: attrs.variationKey,
    turn_index: attrs.turnIndex,
    latency_ms: attrs.latencyMs,
  });
const trackTryItLiveError = (attrs: { errorType: 'rate-limited' | 'run-failed' | 'unknown'; turnIndex: number }) =>
  trackAgentControl('AgentControl Try It Live Error', {
    error_type: attrs.errorType,
    turn_index: attrs.turnIndex,
  });
const trackTryItLiveUnsupportedModelShown = (attrs: { provider: string }) =>
  trackAgentControl('AgentControl Try It Live Unsupported Model Shown', { provider: attrs.provider });
const trackTryItLiveDrawerClosed = (attrs: { userTurnCount: number; duringTour: boolean }) =>
  trackAgentControl('AgentControl Try It Live Drawer Closed', {
    user_turn_count: attrs.userTurnCount,
    during_tour: attrs.duringTour,
  });
const trackTryItLiveResetClicked = () => trackAgentControl('AgentControl Try It Live Reset Clicked');

// Page views
const trackAiConfigListPageViewed = () => trackAgentControl('AgentControl Config List Page Viewed');
const trackAiConfigDetailPageViewed = (attrs: { configKey: string }) =>
  trackAgentControl('AgentControl Config Detail Page Viewed', { config_key: attrs.configKey });
const trackAiConfigMonitoringPageViewed = (attrs: { configKey: string }) =>
  trackAgentControl('AgentControl Config Monitoring Page Viewed', { config_key: attrs.configKey });
const trackAiInsightsPageViewed = () => trackAgentControl('AgentControl AI Insights Page Viewed');

// Agent Graphs
const trackAgentGraphListPageViewed = () => trackAgentControl('AgentControl Graph List Page Viewed');
const trackAgentGraphDetailPageViewed = (attrs: { graphKey: string }) =>
  trackAgentControl('AgentControl Graph Detail Page Viewed', { graph_key: attrs.graphKey });
const trackAgentGraphFTUEStepViewed = (attrs: { step: string }) =>
  trackAgentControl('AgentControl Graph FTUE Step Viewed', { step: attrs.step });
const trackAgentGraphNodeClicked = (attrs: { nodeId: string; nodeType: string }) =>
  trackAgentControl('AgentControl Graph Node Clicked', { node_id: attrs.nodeId, node_type: attrs.nodeType });
const trackAgentGraphMetricFilterChanged = (attrs: { metric: string }) =>
  trackAgentControl('AgentControl Graph Metric Filter Changed', { metric: attrs.metric });
const trackAgentGraphDateRangeChanged = (attrs: { preset?: string; isCustom: boolean }) =>
  trackAgentControl('AgentControl Graph Date Range Changed', attrs);

// Offline Evaluations
const trackEvaluationListPageViewed = () => trackAgentControl('AgentControl Evaluation List Page Viewed');
const trackEvaluationDetailPageViewed = (attrs: { evaluationId: string }) =>
  trackAgentControl('AgentControl Evaluation Detail Page Viewed', { evaluation_id: attrs.evaluationId });
const trackEvaluationUpdated = (attrs: { evaluationId: string }) =>
  trackAgentControl('AgentControl Evaluation Updated', { evaluation_id: attrs.evaluationId });
const trackEvaluationRunDetailPageViewed = (attrs: { evaluationId: string; runId: string }) =>
  trackAgentControl('AgentControl Evaluation Run Detail Page Viewed', {
    evaluation_id: attrs.evaluationId,
    run_id: attrs.runId,
  });
const trackEvaluationSaveAsNewConfigClicked = (attrs: { evaluationId: string }) =>
  trackAgentControl('AgentControl Evaluation Save As New Config Clicked', { evaluation_id: attrs.evaluationId });
const trackEvaluationRunStoppedFromList = (attrs: { evaluationId: string; runId: string }) =>
  trackAgentControl('AgentControl Evaluation Run Stopped From List', {
    evaluation_id: attrs.evaluationId,
    run_id: attrs.runId,
  });

// Datasets
const trackDatasetListPageViewed = () => trackAgentControl('AgentControl Dataset List Page Viewed');
const trackDatasetDetailPageViewed = (attrs: { datasetKey: string }) =>
  trackAgentControl('AgentControl Dataset Detail Page Viewed', { dataset_key: attrs.datasetKey });
const trackDatasetUpdated = (attrs: { datasetKey: string }) =>
  trackAgentControl('AgentControl Dataset Updated', { dataset_key: attrs.datasetKey });

// Prompt Snippets
const trackSnippetListPageViewed = () => trackAgentControl('AgentControl Snippet List Page Viewed');
const trackSnippetDetailPageViewed = (attrs: { snippetKey: string }) =>
  trackAgentControl('AgentControl Snippet Detail Page Viewed', { snippet_key: attrs.snippetKey });
const trackSnippetInsertedIntoVariation = (attrs: { snippetKey: string }) =>
  trackAgentControl('AgentControl Snippet Inserted Into Variation', { snippet_key: attrs.snippetKey });

// Tools
const trackToolListPageViewed = () => trackAgentControl('AgentControl Tool List Page Viewed');
const trackToolDetailPageViewed = (attrs: { toolKey: string }) =>
  trackAgentControl('AgentControl Tool Detail Page Viewed', { tool_key: attrs.toolKey });

// Agent Skills
const trackSkillListPageViewed = () => trackAgentControl('AgentControl Skill List Page Viewed');
const trackSkillDetailPageViewed = (attrs: { skillKey: string }) =>
  trackAgentControl('AgentControl Skill Detail Page Viewed', { skill_key: attrs.skillKey });

// Judge Templates
const trackJudgeTemplateListPageViewed = () => trackAgentControl('AgentControl Judge Template List Page Viewed');
const trackJudgeTemplateDetailPageViewed = (attrs: { templateKey: string }) =>
  trackAgentControl('AgentControl Judge Template Detail Page Viewed', { template_key: attrs.templateKey });
const trackJudgeTemplateUsed = (attrs: { templateKey: string }) =>
  trackAgentControl('AgentControl Judge Template Used', { template_key: attrs.templateKey });

// Agent Optimization
const trackOptimizationListPageViewed = () => trackAgentControl('AgentControl Optimization List Page Viewed');
const trackOptimizationDetailPageViewed = (attrs: { optimizationId: string }) =>
  trackAgentControl('AgentControl Optimization Detail Page Viewed', { optimization_id: attrs.optimizationId });
const trackOptimizationRunDetailPageViewed = (attrs: { optimizationId: string; runId: string }) =>
  trackAgentControl('AgentControl Optimization Run Detail Page Viewed', {
    optimization_id: attrs.optimizationId,
    run_id: attrs.runId,
  });
const trackOptimizationConfigurationChanged = (attrs: { optimizationId: string; field: string }) =>
  trackAgentControl('AgentControl Optimization Configuration Changed', {
    optimization_id: attrs.optimizationId,
    field: attrs.field,
  });

// Config Monitoring
const trackConfigMonitoringDateRangeChanged = (attrs: { configKey: string; preset?: string; isCustom: boolean }) =>
  trackAgentControl('AgentControl Config Monitoring Date Range Changed', {
    config_key: attrs.configKey,
    preset: attrs.preset,
    is_custom: attrs.isCustom,
  });
const trackConfigMonitoringTraceViewed = (attrs: { configKey: string; variationKey?: string }) =>
  trackAgentControl('AgentControl Config Monitoring Trace Viewed', {
    config_key: attrs.configKey,
    variation_key: attrs.variationKey,
  });
const trackConfigMonitoringVariationChanged = (attrs: { configKey: string; variationKey: string }) =>
  trackAgentControl('AgentControl Config Monitoring Variation Changed', {
    config_key: attrs.configKey,
    variation_key: attrs.variationKey,
  });

// Test Run
const trackTestRunStarted = (attrs: { configKey: string; variationKey: string }) =>
  trackAgentControl('AgentControl Test Run Started', {
    config_key: attrs.configKey,
    variation_key: attrs.variationKey,
  });
const trackTestRunContextExpanded = (attrs: { configKey: string }) =>
  trackAgentControl('AgentControl Test Run Context Expanded', { config_key: attrs.configKey });

export {
  trackEmptyStateCreateAIConfigButtonClicked,
  trackEmptyStateLearnMoreButtonClicked,
  trackEmptyStateSandboxButtonClicked,
  trackAIConfigCreateButtonClick,
  trackAIConfigDeleteButtonClicked,
  trackAIConfigSubmitButtonClicked,
  trackAIConfigsLeftNav,
  trackAIMonitoringLeftNav,
  trackJoinWaitlistButtonClicked,
  trackCreateAIConfigButtonClicked,
  trackAIConfigCreateCancelButtonClicked,
  trackAIConfigCreateEditKeyClicked,
  trackModelDropdownClickedAndSelected,
  trackPromptTypeDropdownClickedAndSelected,
  trackDiscardChangesButtonClickedOnVariation,
  trackSaveChangesButtonClickedOnVariation,
  trackRequestApprovalButtonClickedOnVariation,
  trackEditButtonClickedOnVariation,
  trackPreviewButtonClickedOnPrompt,
  trackTemperatureChanged,
  trackAddAnotherVariationButtonClicked,
  trackAIConfigNameUpdated,
  trackAIConfigDescriptionUpdated,
  trackAIConfigTagsUpdated,
  trackAIConfigKeyCopied,
  trackAIConfigCopyLink,
  trackAIConfigChangeHistoryViewed,
  trackAIConfigShowCodeSnippetsClicked,
  trackAIConfigTargetingTabNavigation,
  trackAIConfigMonitoringTabNavigation,
  trackAIConfigPlaygroundsTabNavigation,
  trackAIConfigVariationsTabNavigation,
  trackAIConfigRefreshPendingChangesButtonClicked,
  trackSignupsForAiConfigs,
  trackSignupsForAiConfigsWithoutSDK,
  trackGiveFeedbackButtonClicked,
  trackAIConfigVersionNumberClicked,
  trackAIConfigsViewHistoryButtonClicked,
  trackAIConfigVersionHistoryDrawerOpened,
  trackAIVersioningPromoteClicked,
  trackDuplicateVariationButtonClicked,
  trackAiConfigMonitoringChartSelected,
  trackAiConfigMonitoringTableDataExported,
  trackAiConfigMonitoringChartDataExported,
  trackAiConfigModelRestrictionAdded,
  trackAiConfigModelRestrictionRemoved,
  trackAIMonitoringTrendsTabNavigation,
  trackAiConfigDisplayPreferencesEvent,
  trackEvaluatorMetricsChartExported,
  trackEvaluatorMetricsEvalKeyClicked,

  // AI Config variations
  trackAIConfigVariationAddMessage,
  trackAIConfigVariationAddReviewerButtonClicked,
  trackAIConfigVariationAddVariation,
  trackAIConfigVariationArchiveVariation,
  trackAIConfigVariationCopyMessageToClipboard,
  trackAIConfigVariationDeleteMessage,
  trackAIConfigVariationDiscardChanges,
  trackAIConfigVariationDuplicateVariation,
  trackAIConfigVariationEditMessageFullScreen,
  trackAIConfigVariationSaveSnippetNew,
  trackAIConfigVariationSaveSnippetExisting,
  trackAIConfigVariationEditKeyButtonClicked,
  trackAIConfigVariationMessageRoleChanged,
  trackAIConfigVariationPublishArchiveToggleChanged,
  trackAIConfigVariationRequestApprovalButtonClicked,
  trackAIConfigVariationRestoreVariation,
  trackAIConfigVariationReviewAndSaveButtonClicked,
  trackAIConfigVariationReviewModalCancelled,
  trackAIConfigVariationSaveChangesButtonClicked,
  trackAIConfigVariationVersionNumberClicked,
  trackAIConfigVariationViewHistoryMenu,

  // AI Config sidebar (judges)
  trackAIConfigEventKeyEditButtonClicked,
  trackAIConfigEventKeyUpdated,
  trackAIConfigEventDirectionEditButtonClicked,
  trackAIConfigEventDirectionUpdated,

  // AI Config onboarding
  trackAiOnboardingLoad,
  trackAiConfigsSignupCompleted,
  trackAiOnboardingChoiceMade,
  trackAiSdkKeyCopied,
  trackAiConfigsSampleAppDownloaded,
  trackAiConfigsSdkModelProviderChosen,
  trackAiConfigsSdkFrameworkChosen,
  trackAiConfigsSdkAgenticQuickstartTabViewed,
  trackAiConfigsSdkManualSetupTabViewed,
  trackAiConfigsSdkSampleAppTabViewed,
  trackAiConfigsSdkAgentInstructionsCopied,
  trackAiConfigsSetupSdkViewed,
  trackAiConfigsSetupDeferred,
  trackAiConfigsSetupContinueClicked,
  trackAiConfigsSdkSetupTabSelected,
  trackAiConfigsInstallPromptCopied,
  trackAiConfigCreateStarted,
  trackAiConfigSaveBlocked,
  trackSampleConfigModified,
  trackAiSdkSetupCompleted,
  trackAiPlaygroundToSdkConversion,
  trackTryItLiveToSdkConversion,

  // Playground
  trackAiConfigsPlaygroundApiKeyModalShown,
  trackAiConfigsPlaygroundProviderClicked,
  trackAiConfigsPlaygroundProviderApiKeyEntered,
  trackAiConfigsPlaygroundProviderSettingsSaved,
  trackAiConfigsPlaygroundModelPicked,
  trackAiConfigsPlaygroundPromptEdited,
  trackAiConfigsPlaygroundRun,
  trackAiConfigsPlaygroundFirstRowRendered,
  trackAiConfigsPlaygroundRunCompleted,
  trackAiConfigsPlaygroundPageViewed,
  trackAiConfigsPlaygroundDetailPageViewed,
  trackAgentControl,
  trackAgentControlBannerClicked,
  trackAgentControlOptInEnabled,
  trackAgentControlConfigEvaluated,
  trackAgentControlRunReceived,

  // AI Monitoring Trends
  trackAIMonitoringTrendsDimensionTypeSelected,
  trackAIMonitoringTrendsDimensionOptionsChanged,
  trackAIMonitoringTrendsMetricSelected,
  trackAIMonitoringTrendsDatePresetSelected,
  trackAIMonitoringTrendsCustomDateRangeSelected,
  trackAIMonitoringTrendsTableSearched,
  trackAIMonitoringTrendsTableSorted,
  trackAIMonitoringTrendsTableRowExpanded,
  trackAIMonitoringTrendsConfigLinkClicked,
  trackAIMonitoringTrendsAgentGraphLinkClicked,
  trackAIMonitoringTrendsChartZoomed,

  // Playground onboarding guide
  trackPlaygroundOnboardingWelcomeModalViewed,
  trackPlaygroundOnboardingWelcomeSlideViewed,
  trackPlaygroundOnboardingGetStartedClicked,
  trackPlaygroundOnboardingWelcomeModalDismissed,
  trackPlaygroundOnboardingStepViewed,
  trackPlaygroundOnboardingStepNavigation,
  trackPlaygroundOnboardingCompleted,
  trackPlaygroundOnboardingConfigSaved,
  trackPlaygroundOnboardingConnectAppClicked,

  // Try It Live onboarding guide
  trackTryItLiveOnboardingWelcomeModalViewed,
  trackTryItLiveOnboardingWelcomeSlideViewed,
  trackTryItLiveOnboardingGetStartedClicked,
  trackTryItLiveOnboardingWelcomeModalDismissed,
  trackTryItLiveOnboardingStepViewed,
  trackTryItLiveOnboardingStepNavigation,
  trackTryItLiveOnboardingCompleted,

  // Try It Live — sample config entry points
  trackSampleConfigCtaClicked,
  trackSampleConfigCreated,
  trackNthUserSampleCalloutViewed,
  trackNthUserSampleCalloutDismissed,

  // Try It Live — drawer & chat
  trackTryItLiveDrawerOpened,
  trackTryItLiveMessageSent,
  trackTryItLiveResponseReceived,
  trackTryItLiveError,
  trackTryItLiveUnsupportedModelShown,
  trackTryItLiveDrawerClosed,
  trackTryItLiveResetClicked,

  // Global playgrounds
  trackPlaygroundsListRowOpened,
  trackPlaygroundsListArchiveToggled,
  trackNewPlaygroundClicked,
  trackPlaygroundRunClicked,
  trackPlaygroundDatasetQuickRunSelected,
  trackPlaygroundRunSingleColumnSelected,
  trackPlaygroundRunStopped,
  trackPlaygroundCompareAdded,
  trackPlaygroundRestoredFromBanner,
  trackPlaygroundDatasetSelected,
  trackPlaygroundSaveAsNewAiConfigMenuClicked,
  trackPlaygroundSaveAsCreateVariationClicked,
  trackPlaygroundSaveAsCreateConfigClicked,
  trackPlaygroundSaveAsViewConfigClicked,
  trackPlaygroundOfflineRunTriggered,
  trackPlaygroundLoadConfigClicked,
  trackPlaygroundConfigLoaded,
  trackConfigOpenInPlaygroundClicked,
  trackConfigPlaygroundOpened,
  trackConfigTryInPlaygroundClicked,

  // Page views
  trackAiConfigListPageViewed,
  trackAiConfigDetailPageViewed,
  trackAiConfigMonitoringPageViewed,
  trackAiInsightsPageViewed,

  // Agent Graphs
  trackAgentGraphListPageViewed,
  trackAgentGraphDetailPageViewed,
  trackAgentGraphFTUEStepViewed,
  trackAgentGraphNodeClicked,
  trackAgentGraphMetricFilterChanged,
  trackAgentGraphDateRangeChanged,

  // Offline Evaluations
  trackEvaluationListPageViewed,
  trackEvaluationDetailPageViewed,
  trackEvaluationUpdated,
  trackEvaluationRunDetailPageViewed,
  trackEvaluationSaveAsNewConfigClicked,
  trackEvaluationRunStoppedFromList,

  // Datasets
  trackDatasetListPageViewed,
  trackDatasetDetailPageViewed,
  trackDatasetUpdated,

  // Prompt Snippets
  trackSnippetListPageViewed,
  trackSnippetDetailPageViewed,
  trackSnippetInsertedIntoVariation,

  // Tools
  trackToolListPageViewed,
  trackToolDetailPageViewed,

  // Agent Skills
  trackSkillListPageViewed,
  trackSkillDetailPageViewed,

  // Judge Templates
  trackJudgeTemplateListPageViewed,
  trackJudgeTemplateDetailPageViewed,
  trackJudgeTemplateUsed,

  // Agent Optimization
  trackOptimizationListPageViewed,
  trackOptimizationDetailPageViewed,
  trackOptimizationRunDetailPageViewed,
  trackOptimizationConfigurationChanged,

  // Config Monitoring
  trackConfigMonitoringDateRangeChanged,
  trackConfigMonitoringTraceViewed,
  trackConfigMonitoringVariationChanged,

  // Test Run
  trackTestRunStarted,
  trackTestRunContextExpanded,
};
